import React, { useMemo, useRef } from "react";

import type { ICellRendererParams, TableRef } from "@performyard/py-components";
import { Table, Text } from "@performyard/py-components";
import { toFixed } from "@performyard/utils-isomorphic";

import type { DecisionRow } from "../../../types";
import { DecisionTableContext, IncreaseValues } from "../../MyDecisions/types";

import {
	EDITABLE_TABLE_CLASSNAME,
	READ_ONLY_TABLE_CLASSNAME,
	budgetReviewReadOnlySalaryColumnDefs,
	expandedDecisionTableColumnDefs,
} from "../column-defs";

/** No-op callback used to satisfy required DecisionTableContext handlers in read-only tables. */
export const noop = () => {};

type ExpandedDecisionTableReadOnlyProps = {
	readOnly: true;
	baseRows: DecisionRow[];
	managerId: string;
	recommendedAllotmentPercent?: number | null;
	emptyMessage?: string;
	className?: string;
};

type ExpandedDecisionTableEditableProps = {
	readOnly: false;
	rowData: DecisionRow[];
	context: DecisionTableContext;
	fullWidthCellRenderer: (params: ICellRendererParams<DecisionRow>) => React.ReactNode;
	/** Height of the expanded notes row (default 220 for editable notes UI). */
	superRowHeight?: number;
	tableRefCallback?: (instance: TableRef<DecisionRow> | null) => void;
	className?: string;
};

export type ExpandedDecisionTableProps = ExpandedDecisionTableReadOnlyProps | ExpandedDecisionTableEditableProps;

const DEFAULT_EDIT_SUPER_ROW_HEIGHT = 220;

const ExpandedDecisionTableReadOnly: React.FC<Omit<ExpandedDecisionTableReadOnlyProps, "readOnly">> = ({
	baseRows,
	managerId,
	recommendedAllotmentPercent,
	emptyMessage = "No employees in this budget group.",
	className = READ_ONLY_TABLE_CLASSNAME,
}) => {
	const increaseValues: IncreaseValues = useMemo(() => {
		const merged: IncreaseValues = {};
		for (const row of baseRows) {
			merged[row._id] = {
				percent: row.proposed_increase_percent != null ? toFixed(row.proposed_increase_percent) : null,
				amount: row.proposed_increase_amount != null ? toFixed(row.proposed_increase_amount) : null,
			};
		}
		return merged;
	}, [baseRows]);

	const recommendedIncreasePercent = useMemo(
		() =>
			typeof recommendedAllotmentPercent === "number" && Number.isFinite(recommendedAllotmentPercent)
				? toFixed(recommendedAllotmentPercent)
				: 0,
		[recommendedAllotmentPercent]
	);

	const notesValuesRef = useRef<Record<string, string>>({});

	const gridContext: DecisionTableContext = useMemo(
		() => ({
			increaseValues,
			recommendedIncreasePercent,
			onPercentChange: noop,
			onAmountChange: noop,
			notesValuesRef,
			onNotesChange: noop,
			groupCannotBeSubmitted: null,
			managerId,
			expandedRowId: null,
			onExpand: noop,
			onCollapse: noop,
			readOnlyBudgetReview: true,
		}),
		[increaseValues, recommendedIncreasePercent, managerId]
	);

	if (baseRows.length === 0) {
		return (
			<div className="border-t border-solid border-neutral-2 bg-neutral-0 px-6 py-4">
				<Text size="paragraph-3" weight="regular" color="neutral-6">
					{emptyMessage}
				</Text>
			</div>
		);
	}

	return (
		<div className={`max-w-full min-w-0 overflow-x-auto ${className}`}>
			<Table<DecisionRow>
				rowData={baseRows}
				columnDefs={budgetReviewReadOnlySalaryColumnDefs}
				context={gridContext}
				autoHeight
				animateRows={true}
				getRowId={(params) => params.data._id}
				rowHeight={48}
				headerHeight={48}
			/>
		</div>
	);
};

const ExpandedDecisionTableEditable: React.FC<Omit<ExpandedDecisionTableEditableProps, "readOnly">> = ({
	rowData,
	context,
	fullWidthCellRenderer,
	superRowHeight = DEFAULT_EDIT_SUPER_ROW_HEIGHT,
	tableRefCallback,
	className = EDITABLE_TABLE_CLASSNAME,
}) => (
	<div className={className}>
		<Table<DecisionRow>
			ref={(instance) => {
				tableRefCallback?.(instance);
			}}
			rowData={rowData}
			columnDefs={expandedDecisionTableColumnDefs}
			context={context}
			autoHeight
			animateRows={true}
			getRowId={(params) => params.data._id}
			rowHeight={48}
			headerHeight={48}
			fullWidthCellRenderer={fullWidthCellRenderer}
			isFullWidthRow={(params) => params.rowNode.data?.superRow === true}
			getRowHeight={(params) => (params.data?.superRow ? superRowHeight : 48)}
		/>
	</div>
);

export const ExpandedDecisionTable: React.FC<ExpandedDecisionTableProps> = (props) => {
	const { readOnly, ...rest } = props;
	return readOnly ? (
		<ExpandedDecisionTableReadOnly {...(rest as Omit<ExpandedDecisionTableReadOnlyProps, "readOnly">)} />
	) : (
		<ExpandedDecisionTableEditable {...(rest as Omit<ExpandedDecisionTableEditableProps, "readOnly">)} />
	);
};
